import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import styled from 'styled-components'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  APIProvider,
  AdvancedMarker,
  Map,
  useMap,
} from '@vis.gl/react-google-maps'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import {
  getYards,
  getDashboardMetrics,
  getInventory,
} from '../services/api'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { getGoogleMapsApiKey, getGoogleMapsMapId } from '../config/maps'
import { getMapsLanguage } from '../components/common/mapsLanguage'

/**
 * Styled container for the screen
 */
const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  padding: 0 ${props => props.theme.spacing.sm};
`

/**
 * Dashboard header with refresh button
 */
const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.md};
`

/**
 * Stats grid
 */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  margin-bottom: ${props => props.theme.spacing.md};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

/**
 * Map section container
 */
const MapSection = styled(Card)`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.large} !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
`

/**
 * Map header
 */
const MapHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`

/**
 * Search container
 */
const SearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
`

/**
 * Styled map wrapper
 */
const MapWrapper = styled.div`
  width: 100%;
  min-height: 520px;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  position: relative;
`

/**
 * Map container
 */
const MapContainer = styled.div`
  width: 100%;
  height: 520px;
`

/**
 * Dashboard metrics response
 */
interface DashboardMetricsResponse {
  sitesCount?: number
  totalHives?: number
  liveHives?: number
  inactiveHives?: number
  deadHives?: number
  warehouseHives?: number
}

/**
 * Dashboard metrics
 */
interface DashboardMetrics {
  sitesCount: number
  totalHives: number
  liveHives: number
  inactiveHives: number
  deadHives: number
  warehouseHives: number
}

/**
 * Yard interface
 */
interface Yard {
  id?: string
  name: string
  latitude?: number
  longitude?: number
  hiveCount?: number
  deadHives?: number
  hiveRates?: {
    healthy?: number
    weak?: number
    dead?: number
  }
}

/**
 * Warehouse interface
 */
interface Warehouse {
  _id?: string
  warehouseId: number
  description?: string
  latitude?: number
  longitude?: number
}

/**
 * Map position
 */
interface MapPosition {
  lat: number
  lng: number
}

/**
 * Map bounds
 */
interface MapBounds {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

const DEFAULT_METRICS: DashboardMetrics = {
  sitesCount: 0,
  totalHives: 0,
  liveHives: 0,
  inactiveHives: 0,
  deadHives: 0,
  warehouseHives: 0,
}

// Google Maps libraries - must be constant to prevent reloading
const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places']

type SearchOption = {
  id: string
  label: string
  type: string
  lat?: number
  lng?: number
}

/**
 * Normalizes dashboard metrics
 * @param data - Metrics response
 * @returns Normalized metrics
 */
function normalizeMetrics(data?: DashboardMetricsResponse): DashboardMetrics {
  return {
    sitesCount: data?.sitesCount || 0,
    totalHives: data?.totalHives || 0,
    liveHives: data?.liveHives || 0,
    inactiveHives: data?.inactiveHives || 0,
    deadHives: data?.deadHives || 0,
    warehouseHives: data?.warehouseHives || 0,
  }
}

/**
 * Gets yards with coordinates
 * @param yards - All yards
 * @returns Yards with coordinates
 */
function getYardsWithLocation(yards: Yard[]): Yard[] {
  return yards.filter(yard => yard.latitude && yard.longitude)
}

/**
 * Gets map bounds
 * @param yards - Yards with coordinates
 * @returns Bounds object
 */
function getBounds(yards: Yard[]): MapBounds {
  const lats = yards.map(yard => yard.latitude || 0)
  const lngs = yards.map(yard => yard.longitude || 0)
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  }
}

/**
 * Gets map center
 * @param bounds - Map bounds
 * @returns Center coordinates string
 */
function getCenter(bounds: MapBounds): MapPosition {
  return {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  }
}

/**
 * Gets map zoom level
 * @param bounds - Map bounds
 * @returns Zoom level
 */
function getZoom(bounds: MapBounds): number {
  const delta = Math.max(
    bounds.maxLat - bounds.minLat,
    bounds.maxLng - bounds.minLng
  )
  return Math.max(4, Math.min(14, Math.round(12 - Math.log2(delta * 100 + 1))))
}

/**
 * Formats coordinate precision
 * @param value - Coordinate value
 * @returns Formatted coordinate
 */
function toMapPosition(lat: number, lng: number): MapPosition {
  return { lat, lng }
}

/**
 * Gets map center for sites
 * @param sites - Sites with location
 * @returns Map center
 */
function getMapCenter(sites: Yard[]): MapPosition {
  if (!sites.length) return toMapPosition(32.0853, 34.7818)
  return getCenter(getBounds(sites))
}

/**
 * Gets map zoom for sites
 * @param sites - Sites with location
 * @returns Map zoom
 */
function getMapZoom(sites: Yard[]): number {
  if (!sites.length) return 8
  return getZoom(getBounds(sites))
}

/**
 * Custom renderer for marker clusters — amber brand bubble sized by count.
 * Keeps the map smooth by collapsing many yard pins into a few clusters when
 * zoomed out; zooming in expands them back into the health-colored pins.
 */
const clusterRenderer = {
  render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
    const size = 40 + Math.min(count, 100) / 4
    const div = document.createElement('div')
    div.style.cssText = [
      'display:flex', 'align-items:center', 'justify-content:center',
      `width:${size}px`, `height:${size}px`, 'border-radius:50%',
      'background:radial-gradient(circle at 35% 30%, #ffd76a, #d99a00)',
      'color:#1f2937', 'font-weight:800', `font-size:${count > 99 ? 12 : 14}px`,
      'border:2px solid rgba(255,255,255,0.92)',
      'box-shadow:0 6px 16px rgba(217,154,0,0.45)', 'cursor:pointer',
    ].join(';')
    div.textContent = String(count)
    return new google.maps.marker.AdvancedMarkerElement({ position, content: div })
  },
}

/**
 * Fits map to site bounds
 * @param map - Google map instance
 * @param sites - Sites with location
 * @returns void
 */
function fitMapToSites(map: google.maps.Map, sites: Yard[]): void {
  if (!sites.length) return
  const bounds = new window.google.maps.LatLngBounds()
  sites.forEach(site =>
    bounds.extend(toMapPosition(site.latitude || 0, site.longitude || 0))
  )
  map.fitBounds(bounds)
}

/**
 * Fetches dashboard data
 * @returns Metrics, yards, and warehouses data
 */
async function fetchDashboardData(): Promise<
  [DashboardMetricsResponse, Yard[], Warehouse[]]
> {
  const [metricsRes, yardsRes, inventoryRes] = await Promise.all([
    getDashboardMetrics(),
    getYards(),
    getInventory(),
  ])

  // Filter inventory items that have warehouse IDs and location data
  const warehouses = (inventoryRes.data || []).filter(
    (item: Warehouse) => item.warehouseId && item.latitude && item.longitude
  )

  return [metricsRes.data, yardsRes.data || [], warehouses]
}

/**
 * Fetches dashboard data with fallback
 * @returns Metrics, yards, and warehouses data
 */
async function safeFetchDashboardData(): Promise<
  [DashboardMetricsResponse, Yard[], Warehouse[]]
> {
  try {
    return await fetchDashboardData()
  } catch (error) {
    console.error('Error loading dashboard data:', error)
    return [{}, [], []]
  }
}

/**
 * Applies dashboard data
 * @param data - Metrics, yards, and warehouses data
 * @param setMetrics - Metrics state setter
 * @param setYards - Yards state setter
 * @param setWarehouses - Warehouses state setter
 * @returns void
 */
function applyDashboardData(
  data: [DashboardMetricsResponse, Yard[], Warehouse[]],
  setMetrics: (data: DashboardMetrics) => void,
  setYards: (data: Yard[]) => void,
  setWarehouses: (data: Warehouse[]) => void
): void {
  const [metricsData, yardsData, warehousesData] = data
  setMetrics(normalizeMetrics(metricsData))
  setYards(yardsData)
  setWarehouses(warehousesData)
}

/**
 * Dashboard screen component
 * @returns Dashboard screen
 */
function DashboardMap({
  apiKey,
  mapId,
  sites,
  yards,
  warehouses,
  onYardClick,
}: {
  apiKey: string
  mapId: string
  sites: Yard[]
  yards: Yard[]
  warehouses: Warehouse[]
  onYardClick?: (yardId: string) => void
}): React.JSX.Element {
  return (
    <APIProvider apiKey={apiKey} libraries={GOOGLE_MAPS_LIBRARIES} language={getMapsLanguage()}>
      <MapContent
        mapId={mapId}
        sites={sites}
        yards={yards}
        warehouses={warehouses}
        onYardClick={onYardClick}
      />
    </APIProvider>
  )
}

function MapContent({
  mapId,
  sites,
  yards,
  warehouses,
  onYardClick,
}: {
  mapId: string
  sites: Yard[]
  yards: Yard[]
  warehouses: Warehouse[]
  onYardClick?: (yardId: string) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const map = useMap()
  const [markers, setMarkers] = useState<Record<string, google.maps.marker.AdvancedMarkerElement>>({})
  const clusterer = useRef<MarkerClusterer | null>(null)

  // Create the clusterer once the map is ready
  useEffect(() => {
    if (!map) return
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({ map, renderer: clusterRenderer as any })
    }
  }, [map])

  // Keep the clusterer in sync with the rendered yard markers
  useEffect(() => {
    if (!clusterer.current) return
    clusterer.current.clearMarkers()
    clusterer.current.addMarkers(Object.values(markers))
  }, [markers])

  const setMarkerRef = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => {
      setMarkers(prev => {
        if ((marker && prev[key]) || (!marker && !prev[key])) return prev
        if (marker) return { ...prev, [key]: marker }
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    []
  )

  // Stable ref callback per yard key — prevents React from re-attaching the ref
  // every render (which would loop setState → "Maximum update depth exceeded").
  // NB: a plain object, not a Map — `Map` is shadowed by the imported map component.
  const refCallbacks = useRef<Record<string, (m: google.maps.marker.AdvancedMarkerElement | null) => void>>({})
  const getMarkerRef = useCallback(
    (key: string) => {
      let cb = refCallbacks.current[key]
      if (!cb) {
        cb = (m: google.maps.marker.AdvancedMarkerElement | null) => setMarkerRef(m, key)
        refCallbacks.current[key] = cb
      }
      return cb
    },
    [setMarkerRef]
  )

  useEffect(() => {
    if (!map) return
    ;(window as any).__beeHivesMap = map
    const focus = (window as any).__beeHivesFocus as
      | { lat?: number; lng?: number; zoom?: number }
      | undefined
    if (focus?.lat && focus?.lng) {
      map.panTo({ lat: focus.lat, lng: focus.lng })
      map.setZoom(focus.zoom || 15)
      return
    }
    if (sites.length) {
      fitMapToSites(map, sites)
    }
  }, [map, sites])

  return (
    <Map
      style={{ width: '100%', height: '520px' }}
      defaultCenter={getMapCenter(sites)}
      defaultZoom={getMapZoom(sites)}
      mapTypeId="satellite"
      mapId={mapId || undefined}
      fullscreenControl={false}
      mapTypeControl
      streetViewControl={false}
    >
      {/* Yard markers */}
      {yards
        .filter(yard => yard.latitude && yard.longitude)
        .map((yard, index) => {
          const totalHives = yard.hiveCount || 0
          const deadHives = yard.deadHives || yard.hiveRates?.dead || 0
          const healthyHives = yard.hiveRates?.healthy || 0
          const weakHives = yard.hiveRates?.weak || 0
          const liveHives = totalHives - deadHives
          const healthRate = totalHives > 0 ? Math.round((healthyHives / totalHives) * 100) : 0

          // Device-derived status from gateways/sensors reporting to this site
          const deviceStatus = (yard as any).deviceStatus as 'good' | 'warning' | 'critical' | null | undefined
          const activeDeviceCount = (yard as any).activeDeviceCount as number | undefined

          const isCritical = (deadHives > 0 && deadHives >= totalHives * 0.5) || deviceStatus === 'critical'
          const needsAttention = !isCritical && ((weakHives > 0 && weakHives >= totalHives * 0.3) || deviceStatus === 'warning')

          const deviceText = deviceStatus
            ? t('dashboard.marker.devices', {
                count: activeDeviceCount ?? 0,
                status: t(`dashboard.marker.deviceStatus.${deviceStatus}`),
              })
            : ''
          const tooltipText = t('dashboard.marker.tooltip', {
            name: yard.name,
            total: totalHives,
            live: liveHives,
            dead: deadHives,
            health: healthRate,
          }) + deviceText

          const gradientTop = isCritical ? '#ef5350' : needsAttention ? '#ffb300' : '#f7c948'
          const gradientBot = isCritical ? '#c62828' : needsAttention ? '#f57c00' : '#d99a00'
          const innerTint = isCritical ? 'rgba(255,235,238,0.18)' : needsAttention ? 'rgba(255,243,224,0.18)' : 'rgba(255,248,225,0.18)'
          const iconColor = isCritical ? '#fff5f5' : needsAttention ? '#fff8e1' : '#fffdf7'
          const shadowColor = isCritical ? 'rgba(198,40,40,0.38)' : needsAttention ? 'rgba(245,124,0,0.38)' : 'rgba(217,154,0,0.38)'
          const markerId = `yard-grad-${yard.id || index}`

          return (
            <AdvancedMarker
              key={`yard-${yard.id || index}`}
              ref={getMarkerRef(String(yard.id || index))}
              position={{
                lat: Number(yard.latitude),
                lng: Number(yard.longitude),
              }}
              title={tooltipText}
              onClick={() => onYardClick && yard.id && onYardClick(yard.id)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', filter: `drop-shadow(0 6px 14px ${shadowColor})` }}>
                <div style={{ position: 'relative', width: 54, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="54" height="64" viewBox="0 0 54 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={markerId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={gradientTop} />
                        <stop offset="100%" stopColor={gradientBot} />
                      </linearGradient>
                    </defs>
                    <path d="M27 3C15.402 3 6 12.402 6 24c0 14.2 21 34 21 34s21-19.8 21-34C48 12.402 38.598 3 27 3z" fill={`url(#${markerId})`} />
                    <circle cx="27" cy="24" r="13" fill={innerTint} />
                    <circle cx="27" cy="24" r="10.5" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.24)" strokeWidth="1.2" />
                    <path d="M20.5 17.5H33.5L31.5 20.5H22.5L20.5 17.5Z" fill={iconColor} fillOpacity="0.95" />
                    <rect x="21.5" y="21" width="11" height="3.2" rx="1.6" fill={iconColor} fillOpacity="0.95" />
                    <rect x="20.5" y="25" width="13" height="3.3" rx="1.65" fill={iconColor} fillOpacity="0.9" />
                    <rect x="19.5" y="29.3" width="15" height="3.5" rx="1.75" fill={iconColor} fillOpacity="0.85" />
                    <circle cx="27" cy="31" r="1.6" fill="#8D6E63" />
                  </svg>
                </div>
                <div style={{
                  marginTop: -2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(15,23,42,0.90)',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                  maxWidth: 144,
                  overflow: 'hidden',
                  letterSpacing: 0.15,
                  border: `1px solid ${gradientTop}66`,
                  boxShadow: '0 6px 14px rgba(15,23,42,0.22)',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{yard.name}</span>
                  <span style={{
                    background: 'rgba(255,255,255,0.16)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: 999,
                    padding: '1px 6px',
                    fontSize: 9,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}>
                    {totalHives > 0 ? totalHives : '–'}
                  </span>
                </div>
              </div>
            </AdvancedMarker>
          )
        })}

      {/* Warehouse markers */}
      {warehouses
        .filter(warehouse => warehouse.latitude && warehouse.longitude)
        .map((warehouse, index) => (
          <AdvancedMarker
            key={`warehouse-${warehouse._id || index}`}
            position={{
              lat: Number(warehouse.latitude),
              lng: Number(warehouse.longitude),
            }}
            title={warehouse.description || t('dashboard.warehouseName', { id: warehouse.warehouseId })}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', filter: 'drop-shadow(0 6px 14px rgba(55,71,79,0.42))' }}>
              <svg width="48" height="58" viewBox="0 0 48 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id={`wh-grad-${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#607D8B" />
                    <stop offset="100%" stopColor="#37474F" />
                  </linearGradient>
                </defs>
                <path d="M24 2C13.51 2 5 10.51 5 21c0 13.5 19 35 19 35s19-21.5 19-35C43 10.51 34.49 2 24 2z" fill={`url(#wh-grad-${index})`} />
                <circle cx="24" cy="21" r="13" fill="rgba(255,255,255,0.14)" />
                <circle cx="24" cy="21" r="10.5" fill="rgba(15,23,42,0.18)" stroke="rgba(255,255,255,0.28)" strokeWidth="1.2" />
                <path d="M17 23L24 17L31 23" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="18" y="23" width="12" height="8.5" rx="1.5" fill="white" fillOpacity="0.92" />
                <rect x="22" y="26" width="4" height="5.5" rx="0.8" fill="#455A64" />
              </svg>
              <div style={{
                marginTop: -2,
                background: 'rgba(15,23,42,0.90)',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
                letterSpacing: 0.2,
                border: '1px solid rgba(96,125,139,0.45)',
                boxShadow: '0 6px 14px rgba(15,23,42,0.25)',
                maxWidth: 128,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {warehouse.description || t('dashboard.warehouseShort', { id: warehouse.warehouseId })}
              </div>
            </div>
          </AdvancedMarker>
        ))}
    </Map>
  )
}

function DashboardScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [metrics, setMetrics] = useState<DashboardMetrics>(DEFAULT_METRICS)
  const [yards, setYards] = useState<Yard[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(true)
  const [searchValue, setSearchValue] = useState<any>(null)
  const mapKey = getGoogleMapsApiKey()
  const mapId = getGoogleMapsMapId()

  useEffect(() => {
    loadDashboardData()
  }, [])
  const sites = useMemo(() => getYardsWithLocation(yards), [yards])
  // Search options combining yards and warehouses
  const searchOptions = useMemo(() => {
    const options: SearchOption[] = []

    yards
      .filter(yard => yard.latitude && yard.longitude)
      .forEach((yard, index) => {
        options.push({
          id: `yard-${yard.id || index}`,
          label: t('dashboard.search.yardOption', { name: yard.name }),
          type: 'yard',
          lat: yard.latitude,
          lng: yard.longitude,
        })
      })

    warehouses
      .filter(warehouse => warehouse.latitude && warehouse.longitude)
      .forEach((warehouse, index) => {
        options.push({
          id: `warehouse-${warehouse._id || index}`,
          label: t('dashboard.search.warehouseOption', {
            name: warehouse.description || t('dashboard.warehouseName', { id: warehouse.warehouseId }),
          }),
          type: 'warehouse',
          lat: warehouse.latitude,
          lng: warehouse.longitude,
        })
      })

    return options
  }, [yards, warehouses, t])

  /**
   * Handles search selection
   * @param option - Selected option
   */
  function handleSearchSelect(option: any): void {
    if (option && option.lat && option.lng && window.google?.maps) {
      const target = new window.google.maps.LatLng(option.lat, option.lng)
      const mapInstance = (window as any).__beeHivesMap as
        | google.maps.Map
        | undefined
      if (mapInstance) {
        mapInstance.panTo(target)
        mapInstance.setZoom(15)
      }
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const yardId = params.get('yardId')
    if (!yardId || !yards.length) return
    const targetYard = yards.find(
      yard => String(yard.id) === String(yardId) || String((yard as any)._id) === String(yardId)
    )
    if (!targetYard?.latitude || !targetYard.longitude) return
    const focus = {
      lat: Number(targetYard.latitude),
      lng: Number(targetYard.longitude),
      zoom: 15,
    }
    ;(window as any).__beeHivesFocus = focus
    handleSearchSelect(focus)
    setSearchValue({
      id: `yard-${targetYard.id || yardId}`,
      label: t('dashboard.search.yardOption', { name: targetYard.name }),
      type: 'yard',
      lat: focus.lat,
      lng: focus.lng,
    })
  }, [location.search, yards, t])

  /**
   * Loads dashboard data
   * @returns Promise void
   */
  async function loadDashboardData(): Promise<void> {
    setLoading(true)
    applyDashboardData(
      await safeFetchDashboardData(),
      setMetrics,
      setYards,
      setWarehouses
    )
    setLoading(false)
  }

  const handleRefresh = useCallback(() => {
    loadDashboardData()
  }, [])

  return (
    <Container>
      <DashboardHeader>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('dashboard.title')}
        </Typography>
        <Tooltip title={t('common.refresh')}>
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              transition: 'transform 0.3s',
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </DashboardHeader>
      {loading && !metrics.sitesCount ? (
        <StatsGrid>
          {[0, 1, 2, 3].map(i => (
            <Card key={i} sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton variant="text" width="40%" height={50} sx={{ mx: 'auto' }} />
              </CardContent>
            </Card>
          ))}
        </StatsGrid>
      ) : (
        <>
          <StatsGrid>
            {[
              { label: t('dashboard.kpis.sites'), value: metrics.sitesCount, color: '#1976d2', bg: '#e3f2fd', border: '#bbdefb' },
              { label: t('dashboard.kpis.totalHives'), value: metrics.totalHives, color: '#37474f', bg: '#eceff1', border: '#cfd8dc' },
              { label: t('dashboard.kpis.goodHives'), value: metrics.liveHives, color: '#2e7d32', bg: '#e8f5e9', border: '#c8e6c9' },
              { label: t('dashboard.kpis.normalHives'), value: metrics.inactiveHives, color: '#ed6c02', bg: '#fff3e0', border: '#ffe0b2' },
              { label: t('dashboard.kpis.badHives'), value: metrics.deadHives, color: '#d32f2f', bg: '#fbe9e7', border: '#ffccbc' },
            ].map(kpi => (
              <Card
                key={kpi.label}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: `1px solid ${kpi.border}`,
                  background: kpi.bg,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: kpi.color, fontWeight: 'bold', fontSize: '2.5rem' }}>
                    {kpi.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: kpi.color, mt: 0.5, fontSize: '0.85rem', fontWeight: 600, opacity: 0.85 }}>
                    {kpi.label}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </StatsGrid>
          <MapSection>
            <MapHeader>
              <SearchContainer>
                <Autocomplete
                  options={searchOptions}
                  value={searchValue}
                  onChange={(event, newValue) => {
                    setSearchValue(newValue)
                    handleSearchSelect(newValue)
                  }}
                  getOptionLabel={option => option.label || ''}
                  noOptionsText={t('dashboard.search.noResults')}
                  clearText={t('dashboard.search.clear')}
                  openText={t('dashboard.search.open')}
                  closeText={t('dashboard.search.close')}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={t('dashboard.search.placeholder')}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  )}
                />
              </SearchContainer>
            </MapHeader>
            <MapWrapper>
              <MapContainer>
                {mapKey ? (
                  <DashboardMap
                    apiKey={mapKey}
                    mapId={mapId}
                    sites={sites}
                    yards={yards}
                    warehouses={warehouses}
                    onYardClick={(yardId) => navigate(`/yards/${yardId}`)}
                  />
                ) : (
                  <div>{t('dashboard.missingMapsKey')}</div>
                )}
              </MapContainer>
            </MapWrapper>
          </MapSection>
        </>
      )}
    </Container>
  )

}

export default DashboardScreen
